import { getFiGrowthBook, useIsGrowthBookEnabled } from '../../lib/growthbook';

/**
 * GrowthBook flag that gates the partner landing page redirect in production.
 *
 * - Default: `false` (generic `/products` page is served as before)
 * - Force rule: `{ $or: [{ internalTester: true }, { employee: true }] }` → `true`
 *
 * The flag is only consulted in production. In development & staging builds
 * the redirect is always enabled so Jonathan and the team can iterate on the
 * partner pages without touching GrowthBook.
 */
export const PARTNER_LANDING_FLAG_KEY = 'ecom-partner-landing-pages';

/**
 * Returns whether the BE-3525 partner GwP landing page redirect is enabled.
 *
 * Production → gated by GrowthBook (`ecom-partner-landing-pages`). The flag
 * defaults to `false` and is force-ON for users with `internalTester=true` or
 * `employee=true` during the controlled rollout.
 *
 * Non-production (development, staging, local) → always `true`. Feature flags
 * are not the right mechanism for sandboxing non-prod behavior, and forcing
 * every teammate to individually self-flag as an internalTester in staging
 * would be friction for no benefit.
 */
export function usePartnerLandingEnabled(): boolean {
  return useProductionFlag(PARTNER_LANDING_FLAG_KEY);
}

/**
 * GrowthBook flag for partner pages drawn from a self-serve offer's settings. Off: those slugs behave as before (the
 * hand-built page if there is one, else `/products`). Production only, like the flag above.
 */
export const GWP_OFFER_PAGES_FLAG_KEY = 'ecom-gwp-offer-pages';

export function useGwpOfferPagesEnabled(): boolean {
  return useProductionFlag(GWP_OFFER_PAGES_FLAG_KEY);
}

function useProductionFlag(flagKey: string): boolean {
  const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production';
  const growthBookEnabled = useIsGrowthBookEnabled();

  if (!isProduction) {
    return true;
  }
  if (!growthBookEnabled) {
    return false;
  }
  return getFiGrowthBook().getFeatureValue<boolean>(flagKey, false);
}
